import {
  TableContainer,
  Table,
  TableBody,
  Card,
  TextField,
  InputAdornment,
  Typography,
  Box,
  TablePagination,
  Stack,
  Backdrop,
  CircularProgress,
  alpha,
  Button,
  IconButton,
} from '@mui/material';
import useTableRox from 'src/hooks/useTableRox';
import IconifyLegacy from '../iconify/iconify-legacy';
import ScrollbarLegacy from '../scrollbar/scrollbarlegacy';
import TableNoData from './TableNoData';
import TableHeadCustom from './TableHeadCustom';
import TableRowRox from './TableRowRox';
import TableSelectedActions from './TableSelectedActions';
import React from 'react';
import _, { toSafeInteger } from 'lodash';
import ChipSelectOption from '../chip-select-option/ChipSelectOption';
import moment from 'moment';
import CustomDateRangePickerLegacy from '../custom-date-range-picker/custom-date-range-picker-legacy';
import ReplayIcon from '@mui/icons-material/Replay';

export type ActionProps = {
  icon: string;
  label: string;
  callback: (id: any, v?: boolean) => void;
  type?: string;
};

export type HeaderProps = {
  id: string;
  subId?: string;
  label: string;
  align?: string;
  type?: string;
  icon?: string;
  avatar?: boolean;
  renderCell?: (value: any, row: any) => React.ReactNode;
  actions?: Array<ActionProps>;
  action?: ActionProps;
  mask?: string;
};

type Props = {
  rowsPerPageOptionsArray?: Array<number>;
  data: Array<any>;
  header: Array<HeaderProps>;
  defaultOrderBy: string;
  defaultOrder?: 'asc' | 'desc';
  tableTitle?: string;
  tableSubtitle?: string;
  avatarKey?: string | undefined;
  avatarType?: 'label' | 'icon' | 'default';
  hasSearch?: boolean;
  hasCount?: boolean;
  hasSelect?: boolean;
  selectOptions?: Array<string>;
  defaultSelectedOption?: string;
  hasDatePicker?: boolean;
  labelCount?: string;
  selectKey?: string;
  onClickKey?: string;
  actionKey?: string;
  onClickFunction?: (id: string) => void;
  selectType?: 'all' | 'single' | 'none';
  onSelectRowFunction?: (id: string) => void;
  onSelectAllRowFunction?: (id: string[]) => void;
  defaultSelected?: string[];
  selectedRows?: string[];
  selectActions?: React.ReactNode;
  titleActions?: React.ReactNode;
  disableOnSelect?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  setStartDate?: (date: Date) => void;
  setEndDate?: (date: Date) => void;
  defaultRowsPerPage?: number;
  onChangeRowsPerPageExternal?: (rowsPerPage: number) => void;
  tableDataSize?: number;
  currentPage?: number;
  onChangePageFunction?: (event: unknown, newPage: number) => void;
  isAdvancedReport?: boolean;
  reload?: () => void;
  showTime?: boolean;
  loading?: boolean;
};

export default function TableRox({
  data,
  header,
  avatarKey,
  avatarType,
  defaultOrderBy,
  defaultOrder = 'asc',
  tableTitle,
  tableSubtitle,
  hasSearch = false,
  hasCount = false,
  hasSelect = false,
  selectOptions,
  defaultSelectedOption,
  hasDatePicker = false,
  labelCount = 'Dados',
  selectKey = 'id',
  onClickKey = 'id',
  actionKey = 'selected',
  onClickFunction,
  selectType = 'none',
  onSelectRowFunction,
  onSelectAllRowFunction,
  defaultSelected,
  selectedRows,
  selectActions,
  titleActions,
  disableOnSelect = false,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  defaultRowsPerPage,
  rowsPerPageOptionsArray = [5, 10, 25, 50],
  onChangeRowsPerPageExternal,
  tableDataSize = undefined,
  currentPage = undefined,
  onChangePageFunction,
  isAdvancedReport = false,
  reload,
  showTime = false,
  loading = false,
}: Props) {
  const {
    page,
    order,
    orderBy,
    rowsPerPage,
    //
    selected: internalSelected,
    selectedOption,
    onSelectRow: onInternalSelectRow,
    onSelectAllRows,
    onSelect,
    filterName,
    onFilterName,
    filteredData,
    loadingSearch,
    //
    onSort,
    onChangePage,
    onChangeRowsPerPage,
    //
    // startDate,
    // endDate,
    startDateSearch,
    endDateSearch,
    // setStartDate,
    // setEndDate,
    onCloseDatePicker,
    onApplyDate,
    setOpenDatePicker,
    openDatePicker,
  } = useTableRox({
    defaultOrderBy,
    defaultOrder,
    tableData: data,
    header,
    onSelectRowFunction,
    onSelectAllRowFunction,
    defaultSelected,
    defaultSelectedOption,
    startDate: startDate!,
    endDate: endDate!,
    setStartDate,
    setEndDate,
    defaultRowsPerPage,
  });

  const selected = selectedRows ?? internalSelected;
  const onSelectRow = (id: string) => {
    if (selectedRows !== undefined) {
      onSelectRowFunction?.(id);
    } else {
      onInternalSelectRow(id);
    }
  };

  const dataFiltered: any[] = applySortFilter({
    tableData: filteredData,
    orderBy,
    order,
  });

  return (
    <Card
      sx={{
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}
    >
      <ScrollbarLegacy>
        <TableContainer sx={{ minWidth: 800, mt: 3, position: 'relative' }}>
          <Backdrop
            open={loadingSearch || loading}
            sx={{
              position: 'absolute',
              background: (theme) => `${alpha(theme.palette.grey[800], 0.3)}`,
              zIndex: 2,
            }}
          >
            <CircularProgress color="secondary" size={32} />
          </Backdrop>
          {(tableTitle || titleActions || tableSubtitle) && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ pb: 3, px: 3 }}
            >
              <Stack>
                {tableTitle && tableTitle !== '' && (
                  <Typography variant="h6">{tableTitle}</Typography>
                )}
                {tableSubtitle && tableSubtitle !== '' && (
                  <Typography variant="body1">{tableSubtitle}</Typography>
                )}
              </Stack>
              {titleActions && titleActions}
            </Stack>
          )}
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              {hasSearch && (
                <TextField
                  sx={{
                    ml: 3,
                    mr: 3,
                    maxWidth: 480,
                  }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconifyLegacy icon="ri:search-line" width={18} height={18} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Pesquisar..."
                  onChange={(e) => onFilterName(e)}
                />
              )}
              {hasSelect && selectOptions ? (
                <ChipSelectOption
                  options={selectOptions}
                  selectedOption={selectedOption}
                  onChange={(opt) => onSelect(opt)}
                />
              ) : null}
              {hasDatePicker && startDate && endDate && (
                <>
                  <Box
                    sx={{
                      bgcolor: 'rgba(145, 158, 171, 0.12)',
                      borderRadius: 1,
                      px: 1,
                    }}
                  >
                    <Typography color="primary.main" variant="overline">
                      Período:
                    </Typography>
                    <Button
                      onClick={() => setOpenDatePicker(true)}
                      endIcon={<IconifyLegacy icon="iconamoon:arrow-down-2-light" width={15} />}
                    >
                      <Typography variant="caption">
                        {startDateSearch && endDateSearch
                          ? `${moment(startDateSearch).format(
                              'DD/MM/YY'
                            )} - ${moment(endDateSearch).format('DD/MM/YY')}`
                          : 'Selecionar'}
                      </Typography>
                    </Button>
                  </Box>
                  <CustomDateRangePickerLegacy
                    title="Selecione o período"
                    startDate={startDate}
                    endDate={endDate}
                    onChangeStartDate={(newValue) => {
                      if (newValue) {
                        setStartDate?.(newValue);
                      }
                    }}
                    onChangeEndDate={(newValue) => {
                      if (newValue) {
                        setEndDate?.(newValue);
                      }
                    }}
                    open={openDatePicker}
                    onClose={() => onCloseDatePicker()}
                    onApply={() => onApplyDate()}
                    error={moment(startDate).isAfter(moment(endDate))}
                  />
                </>
              )}

              {reload && (
                <Stack mr={4}>
                  <IconButton onClick={reload} color="primary">
                    <ReplayIcon />
                  </IconButton>
                </Stack>
              )}
            </Stack>
            {hasCount && (
              <Typography
                sx={{
                  pl: 3,
                }}
                variant="body2"
                color="text.secondary"
              >
                {`${filteredData.length} ${labelCount}`}
              </Typography>
            )}
            {selected.length > 0 && selectType === 'all' && (
              <TableSelectedActions
                numSelected={selected.length}
                rowCount={data.length}
                onSelectAllRows={(checked) =>
                  onSelectAllRows(
                    checked,
                    data.map((row) => row[selectKey])
                  )
                }
                actions={selectActions}
              />
            )}
            <Table>
              <TableHeadCustom
                headLabel={selectType === 'single' ? [{ id: '', label: '' }, ...header] : header}
                order={order}
                orderBy={orderBy}
                rowCount={data.length}
                numSelected={selected.length}
                onSort={onSort}
                onSelectAllRows={
                  selectType === 'all'
                    ? (checked) =>
                        onSelectAllRows(
                          checked,
                          data.map((row) => row[selectKey])
                        )
                    : undefined
                }
                sx={
                  selected.length > 0 && selectType === 'all'
                    ? {
                        display: 'none',
                      }
                    : undefined
                }
              />

              <TableBody>
                {(dataFiltered ?? [])
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any, idx) => (
                    <TableRowRox
                      isAdvancedReport={isAdvancedReport}
                      key={row[selectKey]}
                      row={row}
                      header={header}
                      selectType={selectType}
                      selected={selected}
                      selectKey={selectKey}
                      onSelectRow={onSelectRow}
                      onClickKey={onClickKey}
                      onClickFunction={onClickFunction}
                      disableOnSelect={disableOnSelect}
                      avatarKey={avatarKey}
                      avatarType={avatarType}
                      actionKey={actionKey}
                      showTime={showTime}
                    />
                  ))}

                {dataFiltered.length === 0 && (
                  <TableNoData isNotFound={filterName !== ''} filterName={filterName} />
                )}
              </TableBody>
            </Table>
          </Stack>
        </TableContainer>
      </ScrollbarLegacy>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptionsArray}
          component="div"
          count={tableDataSize || dataFiltered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => {
            onChangePage(event, newPage);
            onChangePageFunction?.(event, newPage);
          }}
          onRowsPerPageChange={(e) => {
            onChangeRowsPerPage(e as any);
            onChangeRowsPerPageExternal?.(toSafeInteger(e.target.value));
          }}
          labelRowsPerPage="Resultados por Página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `Mais de ${to}`}`
          }
        />
      </Box>
    </Card>
  );
}

function applySortFilter<T>({
  tableData,
  orderBy,
  order,
}: {
  tableData: T[];
  orderBy: string;
  order: any;
}) {
  return _.orderBy(tableData, orderBy, order);
}
